import type { TokenCredential } from "@azure/identity";
import { LocalCliError } from "./errors.js";

export interface AuthedJsonArgs {
  credential: TokenCredential;
  scope: string;
  method: "GET" | "POST" | "PUT" | "DELETE";
  url: string;
  body?: unknown;
  /** HTTP statuses to treat as success-with-undefined instead of throwing (e.g. 404 for existence checks). */
  okStatuses?: number[];
  /** Per-request ceiling in ms. Defaults to REQUEST_TIMEOUT_MS; 0 disables it. */
  timeoutMs?: number;
}

/**
 * The per-request ceiling, and WHY a request needs one at all.
 *
 * A caller that polls cannot state its own wall-clock bound unless each attempt
 * has one. `m8t foundry await-ready` declared 60 attempts at a 5-second interval —
 * about five minutes of SLEEPING — and was measured taking 34 minutes: roughly 34
 * seconds per attempt that its budget never counted.
 *
 * BE PRECISE ABOUT WHAT THIS FIXES AND WHAT IT DOES NOT. This bounds the FETCH.
 * It does not explain the 34 seconds and does not claim to — undici's 300s
 * default was never the binding constraint (60 x 300s is five hours, not 34
 * minutes), so the measured cost was elsewhere in the attempt. The other
 * unbounded half is the `az account get-access-token` subprocess `getToken` runs
 * BEFORE this fetch, which no request timeout can reach; that half is addressed
 * at the caller (see caching-credential.ts and foundry/await-ready.ts). Together
 * they make attempts x (timeout + interval) an arithmetic a caller can compute.
 * Neither alone does, and the split is stated because a comment claiming this
 * one closed the whole gap is exactly the class of defect that produced it.
 *
 * 30 seconds is well above any healthy ARM or Foundry response and well below the
 * point at which a stuck attempt dominates a poll's budget. It is also what
 * github-app-repo.ts already picked for the same job (GH_REQUEST_TIMEOUT_MS), so
 * this is the repo's existing answer rather than a second one.
 *
 * SCOPE: every caller of this helper does short control-plane work — discovery
 * lists, agent GET/DELETE, role-assignment PUTs. The one genuinely slow Foundry
 * call, createAgentVersion, uses `fetch` directly and is not affected.
 */
export const REQUEST_TIMEOUT_MS = 30_000;

/** Result wrapper exposing the status for callers that branch on 404 etc. */
export interface AuthedJsonResult<T> { status: number; data: T | undefined }

export async function authedJsonResult<T = unknown>(args: AuthedJsonArgs): Promise<AuthedJsonResult<T>> {
  const token = await args.credential.getToken(args.scope);
  if (!token) {
    throw new LocalCliError({
      code: "AUTH_NULL_TOKEN",
      message: `Azure returned no token for scope ${args.scope}.`,
      hint: "Run 'az login' and retry.",
    });
  }
  const timeoutMs = args.timeoutMs ?? REQUEST_TIMEOUT_MS;
  // A probe that hits THIS timeout resets a readiness streak and keeps polling
  // rather than failing the install: AbortSignal.timeout's rejection carries
  // "aborted"/"timeout", which classifyFoundryError matches on the FLATTENED
  // `{status, message}` a poller hands it. It did not before this timeout existed
  // (an Error-gated arm classified the flat shape `unknown`), so the two changes
  // ship together and neither is safe alone.
  //
  // undici surfaces a connection failure as `TypeError: fetch failed` with the
  // real reason on `.cause`, which the classifier cannot see. That is unwrapped
  // at the probe (foundry-ready-probe.ts, describeProbeFailure) rather than here,
  // so the readiness path is covered; a caller of this helper that does not do
  // the same still gets `unknown` from a connection failure.
  const res = await fetch(args.url, {
    method: args.method,
    headers: {
      Authorization: `Bearer ${token.token}`,
      ...(args.body !== undefined ? { "Content-Type": "application/json" } : {}),
    },
    ...(args.body !== undefined ? { body: JSON.stringify(args.body) } : {}),
    ...(timeoutMs > 0 ? { signal: AbortSignal.timeout(timeoutMs) } : {}),
  });

  const ok = res.ok || (args.okStatuses?.includes(res.status) ?? false);
  if (!ok) {
    const text = await res.text().catch(() => "");
    throw new LocalCliError({
      code: "AZURE_HTTP_ERROR",
      message: `${args.method} ${args.url} failed: ${res.status.toString()} ${res.statusText}${text ? ` — ${text.slice(0, 400)}` : ""}`,
    });
  }

  const raw = await res.text();
  const data = raw ? (JSON.parse(raw) as T) : undefined;
  return { status: res.status, data };
}

/** Convenience: returns the parsed body (undefined for empty), throwing on non-2xx. */
export async function authedJson<T = unknown>(args: AuthedJsonArgs): Promise<T | undefined> {
  return (await authedJsonResult<T>(args)).data;
}

/** One page of an ARM list response. */
interface ArmPage<T> {
  value?: T[];
  nextLink?: string;
}

export interface AuthedJsonPagedArgs {
  credential: TokenCredential;
  scope: string;
  url: string;
  /** Safety bound: stop after this many pages rather than trust a server's token forever. */
  maxPages?: number;
}

/**
 * GET an ARM **list** endpoint and return every item across every page.
 *
 * USE THIS FOR ANY ARM LIST. `authedJson` reads a single response, which is a
 * silent correctness bug against a paginated endpoint: ARM splits list results
 * with `nextLink`, and it is under no obligation to put items on the first page.
 * An EMPTY page carrying a continuation token is legal — and real. A caller that
 * reads only page 1 sees zero items and concludes the resources do not exist.
 *
 * Observed live: a subscription-wide Microsoft.CognitiveServices/accounts list
 * returned pages 1-2 empty (each with a $skiptoken) and all three AIServices
 * accounts on page 3, deterministically, at both subscription and RG scope. The
 * un-paginated read left `resolveFoundryProject` blind, so every Foundry endpoint
 * was rejected — including a project created seconds earlier — breaking the
 * installer's deploy-advisors phase and ~20 CLI commands against a live platform.
 * No mocked-ARM unit test produces an empty first page; only a real subscription
 * does. Hence this helper, and hence its "empty page is not the end" test.
 *
 * The page walk is BOUNDED (`maxPages`, default 100): a server that always
 * returns a continuation token must not hang the CLI. Each page is authorized
 * afresh — a continuation URL is still an ARM call — and a non-2xx on any page
 * throws rather than silently truncating the list, because a short list here
 * reads as "the resource does not exist" and sends the user off to create a
 * duplicate.
 */
export async function authedJsonPagedValues<T = unknown>(args: AuthedJsonPagedArgs): Promise<T[]> {
  const maxPages = args.maxPages ?? 100;
  const items: T[] = [];
  let url: string | undefined = args.url;

  for (let page = 0; url && page < maxPages; page++) {
    const body: ArmPage<T> | undefined = await authedJson<ArmPage<T>>({
      credential: args.credential,
      scope: args.scope,
      method: "GET",
      url,
    });
    items.push(...(body?.value ?? []));
    // An empty `value` is NOT a terminator — only the absence of nextLink is.
    url = body?.nextLink;
  }

  return items;
}
